import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, PenLine, Trash2, ShoppingCart, Eye } from 'lucide-react';
import Layout from '../../components/Layout';
import { purchaseItemService, PurchaseItemDTO } from '../../services/purchaseItemService';
import '../../pages/Products/ProductCategoryDetailPage.css';

const PurchaseItemDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [item, setItem] = useState<PurchaseItemDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadItem();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadItem = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await purchaseItemService.getPurchaseItemById(parseInt(id || '0'));
      if (response?.data?.data) {
        setItem(response.data.data);
      }
      setLoading(false);
    } catch {
      setError(t('purchaseItem.failedToLoadItem'));
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="detail-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!item) {
    return (
      <Layout>
        <div className="detail-container">
          <div className="alert alert-danger">{error || t('purchaseItem.itemNotFound')}</div>
          <button className="detail-breadcrumb-btn" onClick={() => navigate('/purchase-items')}>
            {t('purchaseItem.backToList')}
          </button>
        </div>
      </Layout>
    );
  }

  const langCode = t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US';

  return (
    <Layout>
      <div className="detail-container">
        <nav className="detail-breadcrumb">
          <button
            className="detail-breadcrumb-btn"
            onClick={() => navigate('/purchase-items')}
          >
            <ArrowLeft size={16} strokeWidth={2} />
            {t('purchaseItem.backToList')}
          </button>
        </nav>

        <div className="detail-header">
          <div>
            <h1 className="detail-title">{item.productName}</h1>
            <div className="detail-meta">
              <span className="detail-meta-item">
                <code>{item.productSKU}</code>
              </span>
              <span className="detail-meta-item">
                <span className={`badge ${item.isActive ? 'badge-active' : 'badge-inactive'}`}>
                  {item.isActive ? t('purchaseItem.active') : t('purchaseItem.inactive')}
                </span>
              </span>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        <div className="detail-content">
          <div className="detail-main-section">
            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('purchaseItem.productInformation')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <span className="detail-label">{t('purchaseItem.productSKU')}</span>
                    <span className="detail-value"><code>{item.productSKU}</code></span>
                  </div>
                  <div className="detail-field">
                    <span className="detail-label">{t('purchaseItem.productName')}</span>
                    <span className="detail-value">{item.productName}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('purchaseItem.supplierInformation')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <span className="detail-label">{t('purchaseItem.supplierName')}</span>
                    <span className="detail-value">{item.supplierName}</span>
                  </div>
                  <div className="detail-field">
                    <span className="detail-label">{t('purchaseItem.supplierItemCode')}</span>
                    <span className="detail-value">{item.supplierItemCode || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('purchaseItem.pricingOrderDetails')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <span className="detail-label">{t('purchaseItem.purchasePrice')}</span>
                    <span className="detail-value">
                      <strong>{Number(item.purchasePrice).toFixed(2)} {item.currency}</strong>
                    </span>
                  </div>
                  <div className="detail-field">
                    <span className="detail-label">{t('purchaseItem.currency')}</span>
                    <span className="detail-value">{item.currency}</span>
                  </div>
                  <div className="detail-field">
                    <span className="detail-label">{t('purchaseItem.minimumOrderQuantity')}</span>
                    <span className="detail-value">{item.minimumOrderQuantity}</span>
                  </div>
                  <div className="detail-field">
                    <span className="detail-label">{t('purchaseItem.leadTimeDays')}</span>
                    <span className="detail-value">{item.leadTimeDays} {t('common.days') || 'days'}</span>
                  </div>
                  <div className="detail-field">
                    <span className="detail-label">{t('purchaseItem.tax')}</span>
                    <span className="detail-value">
                      {item.taxName
                        ? <span className="badge badge-info">{item.taxName} ({item.taxPercentage}%)</span>
                        : <span style={{ color: 'var(--ims-text-muted)' }}>—</span>}
                    </span>
                  </div>
                </div>
                {item.description && (
                  <div className="detail-field detail-field-full" style={{ marginTop: '16px' }}>
                    <span className="detail-label">{t('purchaseItem.description')}</span>
                    <span className="detail-value">{item.description}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('purchaseItem.auditInformation')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <span className="detail-label">Purchase Item ID</span>
                    <span className="detail-value">{item.purchaseItemId}</span>
                  </div>
                  <div className="detail-field">
                    <span className="detail-label">{t('purchaseItem.createdDate')}</span>
                    <span className="detail-value">
                      {new Date(item.createdDate).toLocaleDateString(langCode)}{' '}
                      {new Date(item.createdDate).toLocaleTimeString(langCode)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="detail-sidebar">
            <div className="detail-card">
              <div className="detail-card-header">
                <h3 className="detail-card-title">{t('common.actions')}</h3>
              </div>
              <div className="detail-card-body">
                <div className="detail-actions">
                  <button
                    className="btn-save"
                    onClick={() => navigate(`/purchase-items/${item.purchaseItemId}/edit`)}
                  >
                    <PenLine size={16} strokeWidth={2} />
                    {t('purchaseItem.editItem')}
                  </button>
                  <button
                    className="btn-delete"
                    onClick={() => navigate(`/purchase-items/${item.purchaseItemId}/delete`)}
                  >
                    <Trash2 size={16} strokeWidth={2} />
                    {t('common.delete')}
                  </button>
                  <button
                    className="detail-btn detail-btn-secondary"
                    onClick={() => navigate('/purchase-items')}
                  >
                    <ArrowLeft size={16} strokeWidth={2} />
                    {t('purchaseItem.backToList')}
                  </button>
                </div>
              </div>
            </div>

            <div className="detail-card">
              <div className="detail-card-header">
                <h3 className="detail-card-title">{t('purchaseItem.quickActions')}</h3>
              </div>
              <div className="detail-card-body">
                <button
                  className="detail-quick-link"
                  onClick={() => navigate(`/products/${item.productId}`)}
                >
                  <Eye size={16} strokeWidth={2} />
                  View Product
                </button>
                <button
                  className="detail-quick-link"
                  onClick={() => navigate('/purchase-orders')}
                >
                  <ShoppingCart size={16} strokeWidth={2} />
                  View Purchase Orders
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PurchaseItemDetailPage;
